<template>
  <div class="crop-elevation-profile">
    <canvas ref="chartCanvas" style="width: 100%; height: auto;"></canvas>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { debounce } from 'lodash-es';
import { markRaw } from 'vue';

export default {
  name: 'RecordingCropAppElevation',
  props: {
    // An array of elevation values (numbers)
    elevations: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  data() {
    return {
      chart: null,
      updatePending: false,
    };
  },
  mounted() {
    this.renderChart();
  },
  watch: {
    // Watch for changes to the elevations prop and update the chart accordingly.
    elevations: {
      handler(newElevations) {
        // Prevent recursive updates
        if (this.updatePending) {
          return;
        }

        // Only update if we have valid data and a chart instance
        if (newElevations && newElevations.length > 0 && this.chart) {
          this.debouncedUpdateChart();
        } else if (newElevations && newElevations.length > 0 && !this.chart) {
          // If chart doesn't exist yet but we now have data, create it
          this.renderChart();
        }
      },
      deep: false,
    },
  },
  created() {
    // Create debounced version of updateChart
    this.debouncedUpdateChart = debounce(this.updateChart, 50);
  },
  methods: {
    renderChart() {
      // Only render if we have data
      if (!this.elevations || this.elevations.length === 0) {
        return;
      }

      const canvas = this.$refs.chartCanvas;
      if (!canvas) {
        return;
      }

      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Destroy existing chart if it exists
        if (this.chart) {
          this.chart.destroy();
        }

        this.chart = markRaw(new Chart(ctx, {
          type: 'line',
          data: {
            // Use the array index as labels (e.g. segment number)
            labels: Array.from({ length: this.elevations.length }, (_, i) => i + 1),
            datasets: [
              {
                label: 'Elevation (m)',
                data: [...this.elevations],
                borderColor: '#fddf00',
                borderWidth: 2,
                backgroundColor: 'rgba(253,233,95,0.2)',
                fill: true,
                tension: 0.1,
                pointRadius: 0,       // Remove points from display
                pointHoverRadius: 0,  // Remove hover effect for points
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            devicePixelRatio: 1, // Ensures a 1:1 pixel ratio
            scales: {
              x: {
                display: true,
                grid: {
                  color: 'rgba(255,255,255,0.1)',
                  lineWidth: 1,
                },
                ticks: {
                  color: '#ffffff',
                  font: {
                    size: 12
                  }
                }
              },
              y: {
                display: true,
                grid: {
                  color: 'rgba(255,255,255,0.1)',
                  lineWidth: 1,
                },
                ticks: {
                  color: '#ffffff',
                  font: {
                    size: 12
                  }
                }
              }
            },
            plugins: {
              legend: {
                display: false,
                labels: {
                  color: '#ffffff'
                }
              }
            },
            animation: {
              duration: 0 // Disable animation for better performance
            }
          },
        }));
      }
    },
    updateChart() {
      // Only update if we have valid data and a chart instance
      if (!this.elevations || this.elevations.length === 0 || !this.chart) {
        return;
      }

      // Prevent recursive updates
      if (this.updatePending) {
        return;
      }

      this.updatePending = true;

      try {
        // Create new arrays to avoid mutation issues
        const newLabels = Array.from({ length: this.elevations.length }, (_, i) => i + 1);
        const newData = [...this.elevations];

        // Update chart data
        this.chart.data.labels = newLabels;
        this.chart.data.datasets[0].data = newData;

        // Update the chart with animation disabled for performance
        this.chart.update('none');
      } catch (error) {
        console.error('Error updating chart:', error);
        // Don't try to recreate - this can cause infinite recursion
      } finally {
        // Reset the flag after a brief delay
        this.$nextTick(() => {
          this.updatePending = false;
        });
      }
    },
  },
  beforeDestroy() {
    // Cancel any pending debounced updates
    if (this.debouncedUpdateChart) {
      this.debouncedUpdateChart.cancel();
    }

    // Clean up chart instance when component is destroyed
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
};
</script>

<style scoped>
.crop-elevation-profile {
  position: absolute;
  bottom: 2em;
  left: 2em;
  right: 2em;
  height: 25vh;
  padding: 1em;
  width: auto;
  margin: 0;
  background-color: rgba(0, 0, 0, 0.85);
  border-radius: 8px;
  z-index: 1;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}
</style>
